/**
 * Detects the user's most likely currency (ISO 4217, e.g. MXN/USD/EUR) using
 * the same pipeline as language detection but pulling `Country.currency` from
 * country-state-city. Falls back to MXN (primary market).
 */
import { Country } from "country-state-city";

const PHONECODE_TO_ISO = (() => {
  const map = {};
  for (const c of Country.getAllCountries()) {
    const code = (c.phonecode || "").toString().replace(/[^0-9]/g, "");
    if (!code) continue;
    if (!map[code]) map[code] = c.isoCode;
  }
  return map;
})();

const TLD_TO_ISO = {
  mx: "MX", es: "ES", ar: "AR", co: "CO", pe: "PE", cl: "CL", ec: "EC",
  ve: "VE", bo: "BO", py: "PY", uy: "UY", gt: "GT", sv: "SV", hn: "HN",
  ni: "NI", cr: "CR", pa: "PA", pr: "PR", do: "DO", cu: "CU",
  us: "US", ca: "CA", uk: "GB", au: "AU", nz: "NZ", ie: "IE",
};

function currencyOf(isoCode) {
  if (!isoCode) return null;
  const c = Country.getAllCountries().find((x) => x.isoCode === isoCode.toUpperCase());
  return c?.currency || null;
}

export function detectCurrencyFromPhone(phone) {
  if (!phone) return null;
  const digits = String(phone).replace(/[^0-9]/g, "");
  if (!digits) return null;
  for (let n = 4; n >= 1; n--) {
    const iso = PHONECODE_TO_ISO[digits.slice(0, n)];
    if (iso) return currencyOf(iso);
  }
  return null;
}

export function detectCurrencyFromEmail(email) {
  if (!email || !email.includes("@")) return null;
  const host = email.split("@")[1].toLowerCase();
  const parts = host.split(".");
  const iso = TLD_TO_ISO[parts[parts.length - 1]];
  return iso ? currencyOf(iso) : null;
}

export function currencyForCountry(isoCode) {
  return currencyOf(isoCode);
}

/** Async — uses backend proxy /api/geo/lang (same endpoint, country_code returned). */
export async function detectCurrencyFromIP() {
  const cacheKey = "dyd_geo_currency";
  const cached = sessionStorage.getItem(cacheKey);
  if (cached) return cached;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2500);
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const res = await fetch(`${base}/api/geo/lang`, { signal: ctrl.signal });
    clearTimeout(t);
    if (!res.ok) return null;
    const data = await res.json();
    const cur = currencyOf(data?.country_code);
    if (cur) sessionStorage.setItem(cacheKey, cur);
    return cur;
  } catch {
    return null;
  }
}

/** Locale-aware formatter. Defaults to es-MX. */
export function formatPrice(amount, currency, locale) {
  const cur = (currency || "MXN").toUpperCase();
  const loc = locale || (cur === "USD" ? "en-US" : "es-MX");
  try {
    return new Intl.NumberFormat(loc, { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(Number(amount || 0));
  } catch {
    return `${cur} ${Number(amount || 0).toFixed(2)}`;
  }
}

export async function detectCurrency({ phone = "", email = "", country = "" } = {}) {
  return (
    detectCurrencyFromPhone(phone) ||
    detectCurrencyFromEmail(email) ||
    (country && currencyOf(country)) ||
    (await detectCurrencyFromIP()) ||
    "MXN"
  );
}
